
import { Component, Vue, Watch } from "vue-property-decorator";
import {Getter} from "vuex-class";
import { ROWS_PER_PAGE } from "@/utils/constants";
import SvgIcon from "@jamescoyle/vue-icon";
import { mdiClose, mdiCheck, mdiAlertCircleOutline, mdiInformationOutline, mdiAlertOutline, mdiRefresh } from "@mdi/js";
import MylobyAutocompleteMultiple from "../../components/generic/autocomplete-multiple.vue";

@Component({
  components: {
    SvgIcon,
    MylobyAutocompleteMultiple,
  },
})

export default class Notifications extends Vue {

    @Getter private currentUser: any;
    @Getter private isMobile: boolean;

    private notifications: any[] = [];
    private totalNotifications: number = 0;
    private loading: boolean = false;
    private onlyUnread: boolean = false;
    private keys: any;
    private selectedKeys: any[] = [];
    private priorities: any;
    private ROWS_PER_PAGE = ROWS_PER_PAGE;

    // Icons
    private mdiClose = mdiClose;
    private mdiCheck = mdiCheck;
    private mdiAlertCircleOutline = mdiAlertCircleOutline;
    private mdiAlertOutline = mdiAlertOutline;
    private mdiInformationOutline = mdiInformationOutline;
    private mdiRefresh = mdiRefresh;

    private pagination: any = {
        page: 1,
        itemsPerPage: 10,
        unread: false,
        priorities: [],
        keys: [],
    };

    @Watch("pagination.page")
    private onPageChange() {
        this.fetchNotifications();
    }

    @Watch("pagination.itemsPerPage")
    private onItemsPerPageChange() {
        this.fetchNotifications(true);
    }

    @Watch("pagination.unread")
    private onUnreadChange() {
        this.fetchNotifications(true);
    }

    @Watch("pagination.priority")
    private onPriorityChange() {
        this.fetchNotifications(true);
    }

    @Watch("selectedKeys")
    private onKeysFilter() {
        this.pagination.keys = this.selectedKeys.map((k) => k.id);
        this.fetchNotifications(true);
    }

    private mounted() {

        if (this.isMobile) {
            this.initScroll();
        } else {
            this.fetchNotifications();
        }
    }

    private async beforeDestroy() {
        if (this.currentUser.id) {
            this.markAllAsRead(false);
        }
    }

    private async fetchKeys({search, page, itemsPerPage}: {search: string, page: number, itemsPerPage: number},
    ): Promise<{ count: number; rows: any[] }> {
        return this.$store.dispatch("users/getUserNotificationKeys", {
            userId: this.currentUser.id,
            pagination: {
                search,
                page,
                itemsPerPage,
            },
        }).then((data) => ({ count: data.count, rows: data.rows }));
    }

    private async fetchNotifications(resetPage = false) {
        if (resetPage) {
            this.pagination.page = 1;
            this.notifications = [];
        }

        this.loading = true;

        try {
            const response = await this.$store.dispatch("users/getUserNotifications", {
                userId: this.currentUser.id,
                pagination: this.pagination,
            });

            if (this.isMobile) {
                if (this.pagination.page === 1) {
                    this.notifications = response.rows;
                } else {
                    this.notifications = [...this.notifications, ...response.rows];
                }
            } else {
                this.notifications = response.rows;
            }

            this.totalNotifications = response.count;

        } catch (error) {
            this.$store.commit("alerts/displayHttpError", { error });
        } finally {
            this.loading = false;
        }
    }

    private async refreshNotifications() {
        try {
            this.fetchNotifications();
        } catch (error) {
            this.$store.commit("alerts/displayHttpError", { error });
        }
    }

    private async handleNotificationClick(item) {
        await this.markAsRead(item);
        try {
                const url = new URL(item.link);
                // Si c'est un lien url dans l'application, on utilise router.push
                if (url.origin === window.location.origin) {
                this.$router.push(url.pathname + url.search + url.hash);
                } else {
                // Si c'est un lien url externe, on charge la page
                window.location.href = item.link;
                }
            } catch (e) {
                // Si c'est déjà adapté pour router.push
                this.$router.push(item.link);
            }
    }

    private async markAllAsRead(fetchNotifications: boolean) {
        try {
            await this.$store.dispatch("users/readUserNotifications", this.currentUser.id);
            this.notifications = this.notifications.map((n) =>   {
                if (n.priority !== "P1") {
                    return {
                    ...n,
                    readAt: new Date(),
                    };
                }
            });
            if (fetchNotifications) {
                await this.fetchNotifications();
            }
        } catch (error) {
            this.$store.commit("alerts/displayHttpError", { error });
        }
    }

    private async deleteAll() {
        try {
            await this.$store.dispatch("users/deleteUserNotifications", this.currentUser.id);
            this.notifications = this.notifications.filter((n) => n.priority !== "P1");
            await this.fetchNotifications();
        } catch (error) {
            this.$store.commit("alerts/displayHttpError", { error });
        }
    }

    private async deleteNotification(notificationId) {
        try {
        await this.$store.dispatch("notifications/delete", notificationId);
        this.notifications = this.notifications.filter((n) => n.id !== notificationId);
        await this.fetchNotifications();
        } catch (error) {
            this.$store.commit("alerts/displayHttpError", { error });
        }
    }

    private async markAsRead(item) {
        try {
            await this.$store.dispatch("notifications/markAsRead", item.id);
            item.readAt = new Date();
            await this.fetchNotifications();
        }  catch (error) {
            this.$store.commit("alerts/displayHttpError", { error });
        }
    }

    private initScroll(): void {
        window.addEventListener("scroll", async (): Promise<void> => {
            const listElm = document.querySelector("#notification-list");
            if (!listElm) { return; }

            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const windowHeight = window.innerHeight;
            const fullHeight = document.documentElement.scrollHeight;

            if (
                scrollTop + windowHeight >= fullHeight - 100
                && !this.loading
                && this.notifications.length < this.totalNotifications
            ) {
                this.pagination.page++;
            }
        });
    }

    private getPriorityColor(priority: string): string {
        switch (priority) {
            case "P1":
                return "red";
            case "P2":
                return "orange";
            case "P3":
                return "green";
            default:
                return "grey";
        }
    }

    private getPriorityIcon(priority: string): string {
        switch (priority) {
            case "P1":
                return mdiAlertOutline;
            case "P2":
                return mdiAlertCircleOutline;
            case "P3":
                return mdiInformationOutline;
            default:
                return "";
        }
    }
}
